import React from "react";
import {
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { useTheme } from "../contexts/ThemeContext";
import type { ThemePreference } from "../contexts/ThemeContext";
import styles from "./ThemeToggle.module.css";

interface ThemeOption {
  value: ThemePreference;
  label: string;
  icon: React.ReactNode;
}

interface ThemeToggleProps {
  className?: string;
  language?: "en" | "ar";
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = "", language }) => {
  const { themePreference, setThemePreference, resolvedTheme } = useTheme();
  const isArabic = language === "ar";
  const resolvedThemeLabel = isArabic
    ? resolvedTheme === "dark" ? "داكن" : "فاتح"
    : resolvedTheme;

  const options: ThemeOption[] = [
    {
      value: "system",
      label: isArabic ? `النظام (${resolvedThemeLabel})` : `System (${resolvedThemeLabel})`,
      icon: <ComputerDesktopIcon aria-hidden="true" />,
    },
    {
      value: "light",
      label: isArabic ? "فاتح" : "Light",
      icon: <SunIcon aria-hidden="true" />,
    },
    {
      value: "dark",
      label: isArabic ? "داكن" : "Dark",
      icon: <MoonIcon aria-hidden="true" />,
    },
  ];

  return (
    <div
      className={[styles.themeToggle, className].filter(Boolean).join(" ")}
      role="group"
      aria-label={isArabic ? "المظهر" : "Theme"}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={[
            styles.themeButton,
            themePreference === option.value ? styles.themeButtonActive : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setThemePreference(option.value)}
          aria-label={isArabic ? `استخدام المظهر ${option.label}` : `Use ${option.label} theme`}
          aria-pressed={themePreference === option.value}
          title={option.label}
        >
          {option.icon}
        </button>
      ))}
    </div>
  );
};

export default ThemeToggle;
